import { PolfEventHoleCompetitionTypeEnum } from '@polf/shared/codegen/types'

import { IconBeatPro, IconClosest, IconLongest } from '../components/Icon'

/**
 * Shared brand identity for the three side-game types. The banner on
 * /score and the cards on /sidegames pull from this table so a
 * CLOSEST competition always shows the gold theme + the closest icon,
 * regardless of which surface renders it.
 *
 * Each brand carries:
 *   - label      Norwegian display name
 *   - Icon       the matching line-icon component
 *   - tileBg     tailwind class for the icon's saturated background
 *                tile (visible on both surfaces)
 *   - bgGradient inline linear-gradient CSS for the banner background
 *   - border     inline rgba border for the banner edge
 *   - helpText   one-line clarification when the label is ambiguous
 *
 * Adding a fourth side-game type later is a one-row addition here
 * plus a new icon in components/Icon.tsx — no surface-level changes.
 */
export type SideGameBrand = {
  label: string
  Icon: (p: { size?: number; className?: string }) => React.ReactElement
  /** Tailwind class for the saturated icon tile (banner + card). */
  tileBg: string
  /** Banner fill — solid two-stop gradient, not transparent. */
  bgGradient: string
  /** Banner edge stroke, matching the tile family. */
  border: string
  /**
   * One-line clarification shown under the brand label on every surface
   * (banner, card, entry form). Used for measurement quirks the label
   * alone can't express — e.g., "Longest drive" is actually scored as
   * distance-to-pin (smallest wins) on this platform because we can't
   * reliably measure raw carry distance on the course. Undefined when
   * the label is already unambiguous.
   */
  helpText?: string
}

export const SIDEGAME_BRAND: Record<
  PolfEventHoleCompetitionTypeEnum,
  SideGameBrand
> = {
  [PolfEventHoleCompetitionTypeEnum.Closest]: {
    label: 'Closest to the pin',
    Icon: IconClosest,
    tileBg: 'bg-gold text-paper',
    bgGradient: 'linear-gradient(180deg, #fbeec7 0%, #f5e1a8 100%)',
    border: 'rgba(200, 151, 59, 0.55)',
  },
  [PolfEventHoleCompetitionTypeEnum.Longest]: {
    label: 'Longest drive',
    Icon: IconLongest,
    tileBg: 'bg-green-700 text-paper',
    bgGradient: 'linear-gradient(180deg, #dceede 0%, #c2dfca 100%)',
    border: 'rgba(20, 84, 58, 0.45)',
    // "Longest drive" is scored as distance-to-pin here — we don't have
    // a measuring crew on the fairway, so the carry-distance gold
    // standard isn't available. Smallest measured distance wins.
    helpText: 'Måles til pinnen — kortest vinner',
  },
  [PolfEventHoleCompetitionTypeEnum.Beatthepro]: {
    label: 'Beat the PRO',
    Icon: IconBeatPro,
    tileBg: 'bg-info text-paper',
    bgGradient: 'linear-gradient(180deg, #dde9fb 0%, #c4d6f3 100%)',
    border: 'rgba(42, 109, 240, 0.45)',
  },
}
